/* eslint-disable i18next/no-literal-string */
import React, { useEffect } from 'react';
import { ShimmerElementType, Stack } from '@fluentui/react';
import { ChevronLeft20Regular as ChevronLeftIcon, ChevronRight20Regular as ChevronRightIcon } from '@fluentui/react-icons';
import clsx from 'clsx';
import { useLocalizedStrings } from 'i18n';

import { IconButton } from '../Buttons/v8';
import { Shimmer } from '../Shimmer';

import { avatarSize, containerHeight, containerWidth as avatarWidth } from './Avatar.styles';
import { AvatarButton, IAvatarButtonProps } from './AvatarButton';
import { tokens, useStyles } from './AvatarButtonRow.styles';

export interface IAvatarButtonRowProps {
  /**
   * Whether or not the component is loaded.
   *
   * @default true
   */
  isLoading?: boolean;
  /**
   * Required array of props for each avatar.
   */
  people: IAvatarButtonProps[];
  /**
   * Optional click handler for left scroll button.
   */
  onScrollLeftClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Optional click handler for right scroll button.
   */
  onScrollRightClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
}
export const AvatarButtonRow: React.FC<IAvatarButtonRowProps> = (props: IAvatarButtonRowProps) => {
  const { isLoading, people, onScrollLeftClick, onScrollRightClick } = props;
  const { t } = useLocalizedStrings();
  const styles = useStyles();

  const [showLeftScroll, setShowLeftScroll] = React.useState(false);
  const [showRightScroll, setShowRightScroll] = React.useState(false);

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // Time in miliseconds to wait for smooth scroll to finish:
  const scrollTimeout = 500;

  // Whether first avatar is hidden by overflow:
  const hasLeftOverflow = (parentElem: HTMLDivElement): boolean => {
    return Math.floor(parentElem.scrollLeft) > 0;
  };

  // Whether last avatar is hidden by overflow:
  const hasRightOverflow = (parentElem: HTMLDivElement): boolean => {
    const parentWidth = parentElem.offsetWidth;
    const parentOverflow = parentElem.scrollLeft;
    const scrollableArea = parentElem.scrollWidth;
    return Math.ceil(parentOverflow + parentWidth) < scrollableArea;
  };

  const scrollLeft = (elem: HTMLDivElement) => {
    elem.scrollBy({
      left: -avatarWidth,
      behavior: 'smooth',
    });
  };

  const scrollRight = (elem: HTMLDivElement) => {
    elem.scrollBy({
      left: avatarWidth,
      behavior: 'smooth',
    });
  };

  const handleLeftClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const wrapper = wrapperRef?.current;
    if (!wrapper) {
      return;
    }
    if (onScrollLeftClick) {
      onScrollLeftClick(event);
    }
    scrollLeft(wrapper);
    setShowRightScroll(true);
    setTimeout(() => setShowLeftScroll(hasLeftOverflow(wrapper)), scrollTimeout);
  };

  const handleRightClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const wrapper = wrapperRef?.current;
    if (!wrapper) {
      return;
    }
    if (onScrollRightClick) {
      onScrollRightClick(event);
    }
    scrollRight(wrapper);
    setShowLeftScroll(true);
    setTimeout(() => setShowRightScroll(hasRightOverflow(wrapper)), scrollTimeout);
  };

  const handleFocus = (index: number) => {
    const wrapper = wrapperRef?.current;
    if (!wrapper) {
      return;
    }
    if (index === 0) {
      // ensure that first item is fully visible even if
      // browser's auto-scroll behavior with tab key doesn't fully show it:
      scrollLeft(wrapper);
      setShowLeftScroll(false);
      setShowRightScroll(hasRightOverflow(wrapper));
    } else if (index + 1 === people.length) {
      // ensure that last item is fully visible even if
      // browser's auto-scroll behavior with tab key doesn't fully show it:
      scrollRight(wrapper);
      setShowLeftScroll(hasLeftOverflow(wrapper));
      setShowRightScroll(false);
    } else {
      setShowLeftScroll(hasLeftOverflow(wrapper));
      setShowRightScroll(hasRightOverflow(wrapper));
    }
  };

  useEffect(() => {
    const wrapper = wrapperRef?.current;
    if (!wrapper) {
      return;
    }
    // Determine if we need a button to scroll right after loading:
    setShowRightScroll(hasRightOverflow(wrapper));
  }, [isLoading]);

  useEffect(() => {
    const onResize = () => {
      const wrapper = wrapperRef?.current;
      if (!wrapper) {
        return;
      }
      // Determine if we need to show buttons after resize:
      setShowLeftScroll(hasLeftOverflow(wrapper));
      setShowRightScroll(hasRightOverflow(wrapper));
    };

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  // TODO: temp loading state, need to get correct state from Design
  const loadingShimmer = () => {
    const shimmerStack = (
      <Stack.Item>
        <Stack tokens={tokens.shimmerInnerStack}>
          <Shimmer
            shimmerElements={[{ type: ShimmerElementType.circle, height: avatarSize, width: avatarSize }]}
            // prevent style bug with white background:
            styles={{ shimmerWrapper: { borderRadius: '100%' } }}
          />
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 12, width: avatarSize }]} />
        </Stack>
      </Stack.Item>
    );

    return (
      <Stack horizontal tokens={tokens.shimmerStack} styles={{ root: { height: containerHeight } }}>
        {shimmerStack}
        {shimmerStack}
        {shimmerStack}
      </Stack>
    );
  };

  return (
    <div className={styles.container}>
      {isLoading ? (
        loadingShimmer()
      ) : (
        <>
          {showLeftScroll && (
            <div className={clsx(styles.buttonWrapper, styles.leftButton)}>
              <IconButton className={styles.button} onClick={handleLeftClick} aria-label={t('Button.ScrollLeft')}>
                <ChevronLeftIcon />
              </IconButton>
              <div className={clsx(styles.blur, styles.leftBlur)} role="presentation" />
            </div>
          )}
          <div className={styles.avatarRowWrapper} ref={wrapperRef} role={undefined}>
            {people.map((person: IAvatarButtonProps, index: number) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={person.name + index}
                className={styles.avatarWrapper}
              >
                <AvatarButton {...person} id={person.name + index} onFocus={() => handleFocus(index)} status={person.status} />
              </div>
            ))}
          </div>
          {showRightScroll && (
            <div className={clsx(styles.buttonWrapper, styles.rightButton)}>
              <IconButton className={styles.button} onClick={handleRightClick} aria-label={t('Button.ScrollRight')}>
                <ChevronRightIcon />
              </IconButton>
              <div className={clsx(styles.blur, styles.rightBlur)} role="presentation" />
            </div>
          )}
        </>
      )}
    </div>
  );
};
